import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';

import type { ExternalInviteApplicationResponseDto } from '@/api/core-api/types.gen';

import { LoadingIndicator } from '@/components/base/loading-indicator/loading-indicator';
import { RecaptchaScript } from '@/hooks/use-recaptcha';
import { parseExternalLinkQuery } from '@/utils/external-link-params';

import { AcceptStep } from './-invite/accept-step';
import { admittedRoute } from './-invite/admitted-route';
import {
  InviteAlreadyCompleted,
  InviteClosed,
  InviteNotFound,
  InviteReceived,
} from './-invite/outcome-screens';
import { externalInviteQueryOptions } from './-invite/queries';
import { getExternalInviteResumeState } from './-invite/resume';

/**
 * Loose on purpose: besides `uid`, the researcher may put any parameters of
 * their own on the link. Nothing reads the parsed search: the router
 * JSON-parses values (`uid=42` becomes a number), so the loader reads the
 * raw query string instead. The schema only keeps the router from rejecting
 * or reshaping the URL.
 */
const searchSchema = z.looseObject({});

/**
 * The landing page behind a project's external invite link: the contact
 * email (for payment), proof that it is theirs (a six-digit code emailed to
 * it), and consent, for every link. Accepting creates the account and files
 * the application. A link that skips the screener admits them straight into
 * the interview; otherwise they answer the screener next, signed in, one
 * question at a time. The project's agreement, when it has one, is accepted
 * before the interview either way. No name is asked for; the person is
 * recorded under the link's uid or their new account id.
 *
 * This browser's external session for the project comes first: an
 * application in progress resumes, one already filed shows the neutral
 * screen, and a completed study turns the link away whatever uid it carries.
 *
 * The uid and the researcher's own parameters on the URL are sent with the
 * application and frozen onto it by core-api when it is filed; a later
 * visit hands back the application as filed, whatever the URL then says.
 */
export const Route = createFileRoute('/participants/_public/invite/$code')({
  validateSearch: searchSchema,
  // The parsed search is the invalidation key only; the values come from
  // the raw query string.
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ params, context, location }) => {
    const { uid, params: linkParams } = parseExternalLinkQuery(
      location.searchStr,
    );

    const invite = await context.queryClient.query(
      externalInviteQueryOptions({ inviteCode: params.code, uid }),
    );

    const resume = await getExternalInviteResumeState({
      data: { projectId: invite.projectId },
    });

    if (resume === 'SCREENER' || resume === 'ADMITTED') {
      throw redirect({
        to:
          resume === 'SCREENER'
            ? '/participants/external/projects/$projectId/screener'
            : admittedRoute(invite.studyType),
        params: { projectId: invite.projectId },
      });
    }

    // Copied to a plain object for the loader's serialisation.
    return { resume, uid, linkParams: { ...linkParams } };
  },
  errorComponent: InviteNotFound,
  component: ExternalInvitePage,
});

function ExternalInvitePage() {
  const { code } = Route.useParams();
  const { resume, uid, linkParams } = Route.useLoaderData();
  const { data: invite } = useQuery(
    externalInviteQueryOptions({ inviteCode: code, uid }),
  );
  const [result, setResult] =
    useState<ExternalInviteApplicationResponseDto | null>(null);

  if (!invite) {
    return <LoadingIndicator />;
  }

  if (resume === 'COMPLETED') {
    return <InviteAlreadyCompleted />;
  }

  if (result || resume === 'RECEIVED') {
    return <InviteReceived projectName={invite.projectName} />;
  }

  if (invite.state !== 'OPEN') {
    return <InviteClosed state={invite.state} />;
  }

  return (
    <>
      <RecaptchaScript />
      <AcceptStep
        code={code}
        uid={uid}
        linkParams={linkParams}
        invite={invite}
        onResult={setResult}
      />
    </>
  );
}
